import type { DailyUptime, Incident } from '../lib/types';

interface Props {
  day: DailyUptime;
  componentId: string;
  incidents: Incident[];
  onClose: () => void;
}

const IMPACT_DOT: Record<string, string> = {
  critical: 'bg-status-major',
  major: 'bg-status-partial',
  minor: 'bg-status-degraded',
  maintenance: 'bg-status-maintenance',
  none: 'bg-[var(--color-fg-subtle)]',
};

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function dayLabel(yyyymmdd: string): string {
  return new Date(yyyymmdd + 'T00:00:00Z').toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// Returns true when `inc` overlaps the UTC calendar day `dayStartMs`.
// Window = [min(createdAt, scheduledFor), max(resolvedAt, scheduledUntil, now)].
function incidentOverlapsDay(inc: Incident, dayStartMs: number): boolean {
  const dayEndMs = dayStartMs + 24 * 60 * 60 * 1000;
  const startMs = new Date(inc.scheduledFor || inc.createdAt).getTime();
  const endIso = inc.resolvedAt || inc.scheduledUntil;
  const endMs = endIso ? new Date(endIso).getTime() : Date.now();
  return startMs < dayEndMs && endMs >= dayStartMs;
}

export function DayDetail({ day, componentId, incidents, onClose }: Readonly<Props>) {
  const dayStartMs = new Date(day.date + 'T00:00:00Z').getTime();
  const matching = incidents.filter(
    (inc) => inc.components.includes(componentId) && incidentOverlapsDay(inc, dayStartMs),
  );

  const pct = day.uptime === null ? null : (day.uptime * 100).toFixed(2);

  return (
    <div className="mt-3 rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] p-4 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-medium text-[var(--color-fg)]">{dayLabel(day.date)}</div>
          <div className="mt-0.5 font-mono text-xs text-[var(--color-fg-muted)]">
            {pct === null ? 'no data' : `${pct}% uptime`}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="font-mono text-xs uppercase tracking-wider text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-fg)]"
          aria-label="Close day detail"
        >
          Close
        </button>
      </div>
      {matching.length === 0 ? (
        <div className="mt-3 text-[var(--color-fg-muted)]">
          No incidents reported on this day.
        </div>
      ) : (
        <ul className="mt-3 space-y-3">
          {matching.map((inc) => (
            <li key={inc.id} className="flex gap-3">
              <span
                className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${
                  IMPACT_DOT[inc.impact] || 'bg-[var(--color-fg-subtle)]'
                }`}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[var(--color-fg)]">{inc.title}</div>
                <div className="mt-0.5 font-mono text-xs text-[var(--color-fg-subtle)]">
                  {timeOf(inc.scheduledFor || inc.createdAt)}
                  {inc.resolvedAt && <> &middot; resolved {timeOf(inc.resolvedAt)}</>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
